import { useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { X, LogOut, Camera, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import AvatarIcon from './AvatarIcon';

export default function SettingsDrawer({ isOpen, onClose, currentCircle }) {
  const { currentUser, userProfile, signOutUser, signInWithGoogle, uploadProfilePhoto } = useAuth();
  const { planId, display, generationsUsed, generationsLimit, remaining, isPaid, isLifetimeLimit } = useSubscription();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleSignOut = async () => {
    if (window.confirm('Sign out of Art Ping Pong?')) {
      await signOutUser();
      onClose();
    }
  };

  const handleAvatarClick = () => {
    if (currentUser?.isAnonymous) {
      if (window.confirm('Link your Google or Apple account to add a profile photo.')) {
        signInWithGoogle();
      }
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be under 5MB');
      return;
    }
    setIsUploading(true);
    try {
      await uploadProfilePhoto(file);
    } catch (err) {
      console.error('Failed to upload profile photo:', err);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <Drawer
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            <DrawerHeader>
              <DrawerTitle>Settings</DrawerTitle>
              <CloseButton onClick={onClose}>
                <X size={20} />
              </CloseButton>
            </DrawerHeader>

            <Section>
              <SectionTitle>Profile</SectionTitle>
              <ProfileRow>
                <AvatarWrapper onClick={handleAvatarClick} $uploading={isUploading}>
                  <AvatarIcon
                    name={userProfile?.displayName}
                    photoURL={userProfile?.photoURL}
                    size={48}
                  />
                  <CameraOverlay>
                    <Camera size={16} />
                  </CameraOverlay>
                  <HiddenInput
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                </AvatarWrapper>
                <ProfileInfo>
                  <ProfileName>
                    {userProfile?.displayName || 'Anonymous'}
                  </ProfileName>
                  <ProfileEmail>
                    {userProfile?.email || (currentUser?.isAnonymous && 'Guest account')}
                  </ProfileEmail>
                </ProfileInfo>
              </ProfileRow>
              {currentUser?.isAnonymous && (
                <LinkButton onClick={signInWithGoogle}>
                  Link with Google to save your account
                </LinkButton>
              )}
            </Section>

            <Section>
              <SectionTitle>Subscription</SectionTitle>
              {(() => {
                const ADMIN_UID = 'o2VVqpqGLvcPeXiNYe8FdGhXnBJ3';
                const isAdmin = currentUser?.uid === ADMIN_UID;
                const isAdminCircle = currentCircle?.createdBy === ADMIN_UID;
                const isUnlimited = isAdmin || isAdminCircle || planId === 'family_pro';
                return (
                  <SubscriptionCard>
                    <SubPlanRow>
                      <SubPlanName>{isAdmin || isAdminCircle ? 'Admin' : display.name}</SubPlanName>
                      {!isAdmin && !isAdminCircle && display.price && <SubPrice>{display.price}</SubPrice>}
                    </SubPlanRow>
                    {!isUnlimited && (
                      <UsageRow>
                        <UsageBar>
                          <UsageFill style={{ width: `${Math.min(100, (generationsUsed / generationsLimit) * 100)}%` }} />
                        </UsageBar>
                        <UsageText>
                          {generationsUsed} / {generationsLimit} generation{generationsLimit !== 1 ? 's' : ''}
                          {isLifetimeLimit ? ' (total)' : ' this month'}
                        </UsageText>
                      </UsageRow>
                    )}
                    {!isUnlimited && !isPaid && (
                      <UpgradeBtn onClick={() => { onClose(); navigate('/pricing'); }}>
                        <Sparkles size={14} />
                        Upgrade
                      </UpgradeBtn>
                    )}
                  </SubscriptionCard>
                );
              })()}
            </Section>

            {currentCircle && (
              <Section>
                <SectionTitle>Active Circle</SectionTitle>
                <CircleInfo>
                  <CircleName>{currentCircle.name}</CircleName>
                  <CircleMeta>
                    {currentCircle.memberIds?.length || 0} member
                    {(currentCircle.memberIds?.length || 0) !== 1 ? 's' : ''}
                  </CircleMeta>
                </CircleInfo>
              </Section>
            )}

            <Spacer />

            <Section>
              <LegalLinks>
                <LegalLink href="https://app.domoreworld.com/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </LegalLink>
                <LegalSeparator>•</LegalSeparator>
                <LegalLink href="https://app.domoreworld.com/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </LegalLink>
              </LegalLinks>
            </Section>

            <SignOutButton onClick={handleSignOut}>
              <LogOut size={18} />
              Sign Out
            </SignOutButton>
          </Drawer>
        </>
      )}
    </AnimatePresence>
  );
}

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 100;
`;

const Drawer = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: min(320px, 85vw);
  background: ${({ theme }) => theme.colors.background};
  z-index: 101;
  display: flex;
  flex-direction: column;
  padding: 20px;
  padding-top: max(20px, env(safe-area-inset-top));
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const DrawerTitle = styled.h2`
  font-size: 20px;
  font-weight: 700;
`;

const CloseButton = styled.button`
  padding: 8px;
  border-radius: ${({ theme }) => theme.radius.small};
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const Section = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textTertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 12px;
`;

const ProfileRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AvatarWrapper = styled.button`
  position: relative;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  opacity: ${({ $uploading }) => ($uploading ? 0.5 : 1)};
  transition: opacity 0.2s;

  &:hover > div:last-of-type {
    opacity: 1;
  }
`;

const CameraOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.2s;
`;

const HiddenInput = styled.input`
  display: none;
`;

const ProfileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ProfileName = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const ProfileEmail = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const LinkButton = styled.button`
  margin-top: 12px;
  padding: 10px 16px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  color: ${({ theme }) => theme.colors.accent};
  font-size: 14px;
  font-weight: 500;
  width: 100%;
  border: 1px solid ${({ theme }) => theme.colors.border};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
  }
`;

const SubscriptionCard = styled.div`
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const SubPlanRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SubPlanName = styled.div`
  font-size: 15px;
  font-weight: 700;
`;

const SubPrice = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const UsageRow = styled.div`
  margin-bottom: 10px;
`;

const UsageBar = styled.div`
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.border};
  margin-bottom: 4px;
  overflow: hidden;
`;

const UsageFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${({ theme }) => theme.colors.accent};
  transition: width 0.3s ease;
`;

const UsageText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const UpgradeBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  border-radius: ${({ theme }) => theme.radius.small};
  background: ${({ theme }) => theme.colors.accent};
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const CircleInfo = styled.div`
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.medium};
  background: ${({ theme }) => theme.colors.surfaceSecondary};
`;

const CircleName = styled.div`
  font-size: 15px;
  font-weight: 600;
`;

const CircleMeta = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: 2px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const LegalLinks = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 12px;
`;

const LegalLink = styled.a`
  color: ${({ theme }) => theme.colors.textSecondary};
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const LegalSeparator = styled.span`
  color: ${({ theme }) => theme.colors.textTertiary};
`;

const SignOutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.medium};
  color: ${({ theme }) => theme.colors.error};
  font-size: 15px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background: rgba(239, 68, 68, 0.05);
  }
`;
